/**
 * Public Trust page — verifiable security claims, no auth required
 * Designed for InfoSec teams + procurement portals.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Shield, CheckCircle2, XCircle, RefreshCcw, Lock, Activity, FileCheck2,
  Server, Eye, ChevronDown, ChevronUp, ArrowRight, Download, FileText,
} from 'lucide-react';
import LiveTrustDashboard from '../components/LiveTrustDashboard';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// User-visible URL. On production this is `https://cartieai.com`,
// on preview it's the preview URL. Never leaks the internal preview
// backend hostname into the customer-facing UI.
const VISITOR_ORIGIN = typeof window !== 'undefined' ? window.location.origin : 'https://cartieai.com';

// Fire-and-forget click tracking. Non-blocking, never throws.
const trackClick = (cta) => {
  try {
    fetch(`${API_URL}/trust/track-click?cta=${encodeURIComponent(cta)}`, {
      method: 'POST', keepalive: true,
    }).catch(() => {});
  } catch (e) { /* no-op */ }
};

const FAQ = [
  {
    q: 'Do I have to give CARTIE my live Stripe secret key?',
    a: (
      <div className="space-y-3 text-slate-300 text-sm">
        <p><strong className="text-emerald-400">No — and we don't want it.</strong> We use Stripe <a href="https://docs.stripe.com/keys#create-restricted-api-secret-key" className="text-emerald-400 underline" target="_blank" rel="noreferrer">Restricted Keys</a> with read-only scope on Customer / Subscription / Invoice. They cannot refund, charge, or access cards. Revocable in 5 seconds.</p>
        <p>Four equally-valid integration paths:</p>
        <ul className="space-y-2 list-none pl-0">
          <li className="p-3 rounded-md bg-emerald-500/5 border border-emerald-500/20"><strong className="text-emerald-300">SMB / Startup:</strong> Stripe Restricted Key, read-only.</li>
          <li className="p-3 rounded-md bg-emerald-500/5 border border-emerald-500/20"><strong className="text-emerald-300">Mid-market / Series B:</strong> Stripe Connect OAuth — customer clicks "Connect Stripe", approves on stripe.com, never pastes a key.</li>
          <li className="p-3 rounded-md bg-emerald-500/5 border border-emerald-500/20"><strong className="text-emerald-300">Enterprise / regulated:</strong> Run our exporter inside your VPC. Only aggregated, hashed identifiers leave your network.</li>
          <li className="p-3 rounded-md bg-emerald-500/5 border border-emerald-500/20"><strong className="text-emerald-300">Paranoid:</strong> Drop a Stripe export CSV once a month.</li>
        </ul>
      </div>
    ),
  },
  {
    q: 'How is my data isolated from other customers?',
    a: (
      <div className="space-y-2 text-slate-300 text-sm">
        <p>Every per-tenant write goes through our <code className="text-emerald-300">tenant_db.py</code> proxy, which physically partitions data into per-org collections. Wrong-org rows are simply not in the collection name we read.</p>
        <p>Every API response carries an <code className="text-emerald-300">X-CARTIE-Org-ID</code> header so even a misconfigured proxy can't silently serve cross-tenant data.</p>
      </div>
    ),
  },
  {
    q: 'Where does CARTIE store my data, and can I delete it?',
    a: (
      <div className="space-y-2 text-slate-300 text-sm">
        <p>MongoDB Atlas, US-East. Encrypted at rest (AES-256), in transit (TLS 1.2+). Account deletion is one click — we delete every per-org collection within 30 days.</p>
        <p>We never sell, share, or use your data to train external models.</p>
      </div>
    ),
  },
  {
    q: 'What about SOC 2 / ISO 27001?',
    a: (
      <div className="space-y-2 text-slate-300 text-sm">
        <p>We follow controls <strong className="text-emerald-400">aligned with</strong> SOC 2 Type I today (stateless cloud creds, encrypted-at-rest, JWT auth with rotation, audit log on every privileged action). Documentation generated from the open-source <a href="https://github.com/strongdm/comply" className="text-emerald-400 underline" target="_blank" rel="noreferrer">strongdm/comply</a> toolkit — <a href={`${VISITOR_ORIGIN}/api/trust/policies.zip`} className="text-emerald-400 underline">download the 15-policy bundle</a>. <strong>We have not yet engaged a third-party auditor</strong> — that begins on customer #25.</p>
        <p>We're evaluating <strong>Drata</strong> and <strong>Vanta</strong> (industry standards for compliance automation) for adoption at customer #25, when we qualify for their startup programs.</p>
        <p>Likely SOC 2 Type II auditors when we engage: Insight Assurance, Prescient, or Sensiba (startup-friendly $7–12K range). ISO 27001 sits in the post-Series-A roadmap.</p>
      </div>
    ),
  },
  {
    q: 'Can I see your security audit results?',
    a: (
      <div className="space-y-2 text-slate-300 text-sm">
        <p>Yes — that's literally what this page is. Click <strong className="text-white">Re-run live check</strong> above to see four wire-level checks evaluated against the live API right now.</p>
        <p>For your procurement team: download the <strong className="text-white">DD pack PDF</strong> — it embeds today's evaluation results.</p>
      </div>
    ),
  },
];

const PublicTrust = () => {
  const [loading, setLoading] = useState(true);
  const [check, setCheck] = useState(null);
  const [headers, setHeaders] = useState(null);
  const [uptime, setUptime] = useState(null);
  const [openIdx, setOpenIdx] = useState(0);
  const [showPersonalize, setShowPersonalize] = useState(false);
  const [personalize, setPersonalize] = useState({ company: '', email: '', name: '' });

  const run = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API_URL}/trust/live-check`),
        fetch(`${API_URL}/trust/headers`),
        fetch(`${API_URL}/trust/uptime`),
      ]);
      const headerSnapshot = {};
      ['x-cartie-org-id', 'x-cartie-tenant-scope', 'strict-transport-security',
       'cross-origin-opener-policy', 'cross-origin-resource-policy',
       'x-frame-options', 'referrer-policy', 'x-content-type-options']
        .forEach((h) => { headerSnapshot[h] = r1.headers.get(h); });
      setHeaders(headerSnapshot);
      setCheck(await r1.json());
      const headersResp = await r2.json();
      setHeaders((prev) => ({ ...prev, _contract: headersResp }));
      setUptime(await r3.json());
    } catch (e) { console.error('Trust check failed:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { run(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div className="min-h-screen bg-slate-950 bg-gradient-to-b from-slate-950 via-slate-950 to-slate-900 text-slate-100" data-testid="trust-page">
      <section className="relative pt-20 pb-14 px-4 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.15),transparent_50%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.10),transparent_50%)] pointer-events-none" />
        <div className="max-w-5xl mx-auto relative">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30"><Shield className="h-3 w-3 mr-1" />Trust & Security</Badge>
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-300">Live · verifiable now</Badge>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5 leading-[1.05]">
            <span className="text-white">Built for InfoSec</span>{' '}
            <span className="text-emerald-400">from day one.</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mb-8 leading-relaxed">Don't take our word for it. Click <strong>Re-run live check</strong> below — every claim on this page is verified against the live API in under one second.</p>

          <div className="flex flex-wrap gap-3">
            <Button onClick={() => { trackClick('rerun_live_check'); run(); }} disabled={loading} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-12 px-6" data-testid="trust-rerun-btn">
              <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />{loading ? 'Running…' : 'Re-run live check'}
            </Button>
            <a
              href={(() => {
                const params = new URLSearchParams();
                if (personalize.company.trim()) params.set('company', personalize.company.trim());
                if (personalize.email.trim()) params.set('email', personalize.email.trim());
                if (personalize.name.trim()) params.set('name', personalize.name.trim());
                const qs = params.toString();
                return `${VISITOR_ORIGIN}/api/trust/dd-pack.pdf${qs ? `?${qs}` : ''}`;
              })()}
              target="_blank" rel="noreferrer" data-testid="trust-dd-pack-link"
            >
              <Button className="h-12 px-6 bg-emerald-500 hover:bg-cyan-400 text-white font-bold"><Download className="h-4 w-4 mr-2" />Download DD pack (PDF)</Button>
            </a>
            <Button variant="outline" onClick={() => { trackClick('personalise_toggle'); setShowPersonalize(!showPersonalize); }} className="h-12 px-5 border-white/20 text-slate-200 hover:bg-white/5" data-testid="trust-personalize-toggle">
              {showPersonalize ? 'Hide' : 'Personalise cover'}
            </Button>
            <a href={`${VISITOR_ORIGIN}/api/trust/live-check`} target="_blank" rel="noreferrer" onClick={() => trackClick('raw_json')} data-testid="trust-curl-link">
              <Button variant="outline" className="h-12 px-6 border-white/20 text-slate-200 hover:bg-white/5"><Eye className="h-4 w-4 mr-2" />Raw JSON</Button>
            </a>
            <a href={`${VISITOR_ORIGIN}/api/trust/policies.zip`} rel="noreferrer" data-testid="trust-policies-link">
              <Button variant="outline" className="h-12 px-6 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/5 hover:border-emerald-500">
                <FileText className="h-4 w-4 mr-2" />Policy library (ZIP · 15 policies)
              </Button>
            </a>
          </div>

          {showPersonalize && (
            <div className="mt-5 p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/30 max-w-3xl" data-testid="personalize-panel">
              <p className="text-sm text-slate-300 mb-3">Personalise the PDF cover for your InfoSec reviewer. <span className="text-slate-400">All optional. Email is logged so we know which packets get used.</span></p>
              <div className="grid sm:grid-cols-3 gap-3">
                <input type="text" placeholder="Company (e.g. Acme Corp)" value={personalize.company} onChange={(e) => setPersonalize({ ...personalize, company: e.target.value })} className="px-3 py-2 rounded-md bg-slate-900 border border-white/10 text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none" data-testid="personalize-company" />
                <input type="text" placeholder="Reviewer name" value={personalize.name} onChange={(e) => setPersonalize({ ...personalize, name: e.target.value })} className="px-3 py-2 rounded-md bg-slate-900 border border-white/10 text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none" data-testid="personalize-name" />
                <input type="email" placeholder="Reviewer email" value={personalize.email} onChange={(e) => setPersonalize({ ...personalize, email: e.target.value })} className="px-3 py-2 rounded-md bg-slate-900 border border-white/10 text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none" data-testid="personalize-email" />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Live aggregate metrics (v35.06) — proves the platform is actually in use */}
      <LiveTrustDashboard />

      <section className="py-12 px-4 border-b border-white/5">
        <div className="max-w-5xl mx-auto space-y-5">
          {loading ? <Skeleton className="h-32 bg-white/5" /> : check ? (
            <Card className={`border-2 ${check.all_passed ? 'border-emerald-500/60 bg-gradient-to-br from-emerald-500/10 via-transparent to-emerald-500/5' : 'border-rose-500/60 bg-gradient-to-br from-rose-500/10 to-transparent'}`} data-testid="live-check-overall">
              <CardContent className="p-7 flex items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  {check.all_passed ? <CheckCircle2 className="h-14 w-14 text-emerald-400 shrink-0" /> : <XCircle className="h-14 w-14 text-rose-400 shrink-0" />}
                  <div>
                    <h2 className={`text-2xl sm:text-3xl font-bold ${check.all_passed ? 'text-emerald-300' : 'text-rose-300'}`}>{check.all_passed ? 'All security checks passed' : 'Issues detected'}</h2>
                    <p className="text-sm text-slate-300 mt-1">{check.passed} of {check.total} guarantees verified · ran in <span className="font-mono text-emerald-300">{check.duration_ms}ms</span> · evaluated {new Date(check.evaluated_at).toLocaleString()}</p>
                  </div>
                </div>
                <div className="text-right shrink-0"><div className={`text-4xl font-bold ${check.all_passed ? 'text-emerald-300' : 'text-rose-400'}`}>{check.passed}/{check.total}</div><p className="text-xs text-slate-400 uppercase tracking-wider mt-1">live</p></div>
              </CardContent>
            </Card>
          ) : null}

          <div className="grid sm:grid-cols-2 gap-3" data-testid="live-check-list">
            {(check?.checks || []).map((c) => (
              <Card key={c.id} className={`bg-white/5 border ${c.passed ? 'border-emerald-500/30' : 'border-rose-500/40'}`}>
                <CardContent className="p-4 flex items-start gap-3">
                  {c.passed ? <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" /> : <XCircle className="h-5 w-5 text-rose-400 mt-0.5 shrink-0" />}
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-white">{c.title}</p>
                    <code className="text-[11px] text-slate-400">{c.id}</code>
                    {c.mode && <p className="text-xs text-slate-300 mt-1"><span className="text-slate-400">mode:</span> {c.mode} · <span className="text-slate-400">key type:</span> {c.type}</p>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 px-4 border-b border-white/5">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-5">
          <Card className="bg-white/5 border-white/10" data-testid="uptime-card">
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg text-white"><Activity className="h-5 w-5 text-emerald-400" />Uptime & status</CardTitle><CardDescription className="text-slate-400">Service availability — last 30 / 90 days.</CardDescription></CardHeader>
            <CardContent>
              {uptime ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3"><div className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" /><span className="text-emerald-300 font-semibold uppercase text-xs tracking-wider">{uptime.status}</span></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-3xl font-bold text-emerald-300">{uptime.uptime_30d_pct}%</p><p className="text-xs text-slate-400 uppercase tracking-wider">30-day uptime</p></div>
                    <div><p className="text-3xl font-bold text-emerald-300">{uptime.uptime_90d_pct}%</p><p className="text-xs text-slate-400 uppercase tracking-wider">90-day uptime</p></div>
                  </div>
                  <p className="text-xs text-slate-400">{uptime.incidents_last_30d} incidents in the last 30 days · health pings today: {uptime.health_pings_today}</p>
                </div>
              ) : <Skeleton className="h-32 bg-white/5" />}
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10" data-testid="wire-headers-card">
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg text-white"><Lock className="h-5 w-5 text-cyan-400" />Wire-level proof</CardTitle><CardDescription className="text-slate-400">The headers your browser just received.</CardDescription></CardHeader>
            <CardContent>
              {headers ? <pre className="text-[11px] bg-slate-950 border border-white/5 rounded-lg p-3 overflow-x-auto font-mono text-emerald-300">{Object.entries(headers).filter(([k, v]) => v && k !== '_contract').map(([k, v]) => `${k}: ${v}`).join('\n')}</pre> : <Skeleton className="h-32 bg-white/5" />}
              <p className="text-xs text-slate-400 mt-3 flex items-start gap-2"><FileCheck2 className="h-4 w-4 shrink-0 mt-0.5 text-cyan-400" /><span>Verify yourself: <code className="text-cyan-300">curl -I {VISITOR_ORIGIN}/api/trust/live-check</code></span></p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-12 px-4 border-b border-white/5" data-testid="compliance-roadmap">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">Compliance & certification roadmap</h2>
          <p className="text-slate-400 mb-8 max-w-2xl">Where we are, where we're going, and the controls already in place. Truthful framing — we don't claim certifications we don't have.</p>
          <div className="space-y-3">
            {[
              { state: 'live', title: 'SOC 2-aligned controls', desc: 'Following SOC 2 Type I controls (stateless cloud creds · encrypted-at-rest · JWT auth with rotation · audit log on every privileged action). Documentation generated from the open-source strongdm/comply toolkit.' },
              { state: 'live', title: 'GDPR-aligned data handling', desc: '30-day deletion on account close · per-tenant DEK · DPA available on request.' },
              { state: 'live', title: 'Multi-tenant isolation', desc: 'Physical per-org MongoDB collections · X-CARTIE-Org-ID watermark on every response · 60+ leak_guard tests on every CI run.' },
              { state: 'planned', title: 'Compliance automation platform', desc: 'Evaluating Drata and Vanta (industry standards) for evidence automation. Adoption begins at customer #25 — qualifying for the startup program in either at that scale.' },
              { state: 'planned', title: 'SOC 2 Type II audit', desc: 'CPA auditor selection (likely Insight Assurance, Prescient, or Sensiba — startup-friendly $7–12K range) begins at customer #25. Typical observation window 6–12 months.' },
              { state: 'planned', title: 'ISO 27001', desc: 'Post-Series-A roadmap once SOC 2 Type II is in hand.' },
              { state: 'planned', title: 'HIPAA-aligned BAA', desc: 'On request for healthcare customers post-Series-A.' },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-3 p-4 rounded-lg bg-white/5 border border-white/10">
                {item.state === 'live' ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 shrink-0 mt-0.5">LIVE</Badge> : <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 shrink-0 mt-0.5">PLANNED</Badge>}
                <div><p className="font-semibold text-white">{item.title}</p><p className="text-sm text-slate-400 mt-1">{item.desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 px-4 border-b border-white/5" data-testid="trust-faq">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">Most-asked questions</h2>
          <p className="text-slate-400 mb-8">Answers we wish came pre-packaged with every InfoSec questionnaire.</p>
          <div className="space-y-3">
            {FAQ.map((item, idx) => {
              const isOpen = openIdx === idx;
              return (
                <Card key={idx} className="bg-white/5 border-white/10 hover:border-emerald-500/30 transition-colors">
                  <button className="w-full text-left p-5 flex items-start justify-between gap-4 text-white" onClick={() => setOpenIdx(isOpen ? -1 : idx)} data-testid={`faq-toggle-${idx}`}>
                    <span className="font-semibold text-base sm:text-lg">{item.q}</span>
                    {isOpen ? <ChevronUp className="h-5 w-5 text-slate-400 shrink-0 mt-1" /> : <ChevronDown className="h-5 w-5 text-slate-400 shrink-0 mt-1" />}
                  </button>
                  {isOpen && <div className="px-5 pb-5" data-testid={`faq-answer-${idx}`}>{item.a}</div>}
                </Card>
              );
            })}
          </div>

          <div className="mt-10 p-5 rounded-lg bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Server className="h-6 w-6 text-emerald-400 shrink-0 mt-1" />
              <div><p className="font-semibold text-white">Need our SIG-Lite or a signed DPA?</p><p className="text-sm text-slate-400">We respond to InfoSec questionnaires within 1 business day.</p></div>
            </div>
            <Link to="/contact" data-testid="trust-contact-cta">
              <Button className="bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400">Email security@cartieai.com<ArrowRight className="h-4 w-4 ml-2" /></Button>
            </Link>
          </div>
        </div>
      </section>

      <div className="text-center py-8 text-xs text-slate-400">Last evaluated: {check ? new Date(check.evaluated_at).toLocaleString() : '—'} · <Link to="/" className="hover:text-emerald-400">Back to CARTIEAI</Link></div>
    </div>
  );
};

export default PublicTrust;
